<template>
  <!-- Month Details Modal -->
  <BaseModal
    :is-visible="show"
    :title="`${monthData?.name} ${year}`"
    subtitle="Module rotation details"
    :icon="Calendar"
    size="2xl"
    type="default"
    :show-footer="false"
    @close="$emit('close')"
  >
        <!-- Module Information -->
        <div v-if="monthData?.module" class="mb-8">
          <div class="flex items-start space-x-4 mb-6">
            <div 
              class="flex-shrink-0 w-4 h-4 rounded-full mt-1"
              :class="getModuleStatusColor(monthData.module)"
            ></div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-primary-text dark:text-primary-dark-text mb-2">
                {{ monthData.module.module_name }}
              </h3>
              <p 
                class="text-sm mb-4"
                :class="getModuleStatusTextColor(monthData.module)"
              >
                {{ getModuleStatusText(monthData.module) }}
              </p>
            </div>
          </div>

          <!-- Module Details Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <!-- Date Information -->
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 class="font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                <Calendar class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Schedule
              </h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-blue-600 dark:text-blue-300">Start Date:</span>
                  <span class="text-blue-900 dark:text-blue-100 font-medium">
                    {{ formatDate(monthData.module.start_date) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-blue-600 dark:text-blue-300">End Date:</span>
                  <span class="text-blue-900 dark:text-blue-100 font-medium">
                    {{ formatDate(monthData.module.end_date) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-blue-600 dark:text-blue-300">Duration:</span>
                  <span class="text-blue-900 dark:text-blue-100 font-medium">
                    {{ getDuration(monthData.module.start_date, monthData.module.end_date) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Time Progress Information -->
            <div class="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <h4 class="font-medium text-purple-900 dark:text-purple-100 mb-3 flex items-center">
                <Clock class="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                Time Progress
              </h4>
              <div class="space-y-3">
                <div class="flex justify-between text-sm">
                  <span class="text-purple-600 dark:text-purple-300">Month Duration:</span>
                  <span class="text-purple-900 dark:text-purple-100 font-medium">
                    {{ getMonthProgress(monthData.module) }}%
                  </span>
                </div>
                <div class="w-full bg-purple-200 dark:bg-purple-800 rounded-full h-3">
                  <div 
                    class="h-3 rounded-full transition-all duration-300"
                    :class="getProgressBarColor(monthData.module)"
                    :style="{ width: getMonthProgress(monthData.module) + '%' }"
                  ></div>
                </div>
                <div class="text-xs text-purple-600 dark:text-purple-300">
                  {{ getProgressDescription(monthData.module) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Module Status -->
          <div class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
            <h4 class="font-medium text-emerald-900 dark:text-emerald-100 mb-3 flex items-center">
              <Info class="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
              Module Status
            </h4>
            <div class="flex items-center space-x-2">
              <div 
                class="w-3 h-3 rounded-full"
                :class="getModuleStatusColor(monthData.module)"
              ></div>
              <span 
                class="text-sm font-medium"
                :class="getModuleStatusTextColor(monthData.module)"
              >
                {{ getModuleStatusText(monthData.module) }}
              </span>
            </div>
          </div>
        </div>

        <!-- No Module Assigned -->
        <div v-else class="text-center py-8 mb-8">
          <div class="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
            <Calendar class="w-16 h-16 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
              No Module Assigned
            </h3>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              No training module is scheduled for {{ monthData?.name }} {{ year }}
            </p>
          </div>
        </div>

        <!-- Employee Progress Section -->
        <div v-if="monthData?.module" class="border-t border-gray-200 dark:border-gray-700 pt-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h4 class="text-lg font-medium text-primary-text dark:text-primary-dark-text flex items-center">
                <Users class="w-5 h-5 mr-2 text-primary-accent dark:text-primary-dark-accent" />
                Employee Progress
              </h4>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text mt-1">
                Track employee module assignments and exam progress
              </p>
            </div>
            <button
              @click="refreshEmployeeProgress"
              :disabled="loadingProgress"
              class="px-3 py-2 text-sm bg-primary-accent dark:bg-primary-dark-accent text-white rounded-lg hover:bg-primary-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': loadingProgress }" />
              <span>{{ loadingProgress ? 'Loading...' : 'Refresh' }}</span>
            </button>
          </div>

          <!-- Progress Summary -->
          <div v-if="employeeProgress" class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div class="text-2xl font-bold text-blue-700 dark:text-blue-300">{{ employeeProgress.summary.employees_with_access }}</div>
              <div class="text-sm text-blue-600 dark:text-blue-400">Assigned Employees</div>
            </div>
            <div class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
              <div class="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{{ employeeProgress.summary.exams_taken }}</div>
              <div class="text-sm text-emerald-600 dark:text-emerald-400">Exams Taken</div>
            </div>
            <div class="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div class="text-2xl font-bold text-purple-700 dark:text-purple-300">{{ employeeProgress.summary.completion_rate }}%</div>
              <div class="text-sm text-purple-600 dark:text-purple-400">Completion Rate</div>
            </div>
            <div class="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div class="text-2xl font-bold text-amber-700 dark:text-amber-300">{{ employeeProgress.summary.pass_rate }}%</div>
              <div class="text-sm text-amber-600 dark:text-amber-400">Pass Rate</div>
            </div>
          </div>

          <!-- Employee List -->
          <div v-if="employeeProgress && employeeProgress.employee_progress.length > 0" class="space-y-4">
            <div
              v-for="employee in employeeProgress.employee_progress"
              :key="employee.employee_id"
              class="border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:shadow-lg hover:border-primary-accent/30 dark:hover:border-primary-dark-accent/30 transition-all duration-200"
            >
              <!-- Employee Header -->
              <div class="flex items-center justify-between mb-4 employee-row">
                <div class="flex items-center space-x-4 employee-info">
                  <!-- Employee Avatar -->
                  <div class="relative">
                    <div class="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-primary-accent/20 to-primary-accent/40 flex items-center justify-center border-2 border-primary-accent/30">
                      <img
                        v-if="employee.employee.user_image"
                        :src="employee.employee.user_image"
                        :alt="employee.employee.user_details?.full_name || 'Employee'"
                        class="h-full w-full object-cover"
                        @error="handleImageError"
                      />
                      <div v-else-if="employee.employee.user_details" class="text-primary-accent dark:text-primary-dark-accent text-lg font-medium">
                        {{ employee.employee.user_details.first_name?.charAt(0) || '' }}{{ employee.employee.user_details.last_name?.charAt(0) || '' }}
                      </div>
                      <User v-else class="w-6 h-6 text-primary-accent dark:text-primary-dark-accent" />
                    </div>
                    <!-- Access Status Indicator -->
                    <div 
                      class="absolute bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-dark-secondary flex items-center justify-center"
                      :class="employee.has_access ? 'bg-emerald-500' : 'bg-gray-400'"
                    >
                      <UserCheck v-if="employee.has_access" class="w-2.5 h-2.5 text-white" />
                      <UserX v-else class="w-2.5 h-2.5 text-white" />
                    </div>
                  </div>
                  
                  <!-- Employee Info -->
                  <div class="flex-1">
                    <h5 class="font-semibold text-primary-text dark:text-primary-dark-text text-lg">
                      {{ employee.employee.user_details.full_name }}
                    </h5>
                    <div class="flex items-center space-x-2 text-sm text-secondary-text dark:text-secondary-dark-text">
                      <Mail class="w-4 h-4" />
                      <span>{{ employee.employee.email }}</span>
                    </div>
                    <div v-if="employee.assignment" class="flex items-center space-x-2 text-xs text-tertiary-text dark:text-tertiary-dark-text mt-1">
                      <span>Assigned: {{ getMonthNames(employee.assignment.accessible_months).join(', ') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Overall Status Badge -->
                <div class="employee-badge">
                  <div 
                    class="flex items-center px-3 py-1.5 rounded-full text-xs font-medium border"
                    :class="[getEmployeeStatusClass(employee), { 'status-completed': employee.progress.exam_passed, 'status-failed': employee.progress.exam_taken && !employee.progress.exam_passed }]"
                  >
                    <component 
                      :is="getEmployeeStatusIcon(employee)" 
                      class="status-icon w-3 h-3 mr-2 inline-block"
                    />
                    <span class="status-text">{{ getEmployeeStatusText(employee) }}</span>
                  </div>
                </div>
              </div>

              <!-- Progress Details -->
              <div v-if="employee.progress.exam_available" class="grid grid-cols-1 md:grid-cols-4 gap-4 rounded-lg p-4">
                <!-- Exam Status -->
                <div class="flex items-center space-x-3">
                  <div 
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    :class="employee.progress.exam_taken ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-800'"
                  >
                    <Clock3 v-if="!employee.progress.exam_taken" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <CheckCircle2 v-else-if="employee.progress.exam_passed" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <XCircle v-else class="w-5 h-5 text-red-500 dark:text-red-400" />
                  </div>
                  <div>
                    <div class="text-sm font-medium text-primary-text dark:text-primary-dark-text">
                      {{ employee.progress.exam_taken ? 'Exam Completed' : 'Exam Pending' }}
                    </div>
                    <div 
                      class="text-xs"
                      :class="!employee.progress.exam_taken ? 'text-gray-500 dark:text-gray-400' : 
                              employee.progress.exam_passed ? 'text-emerald-600 dark:text-emerald-400' : 
                              'text-red-500 dark:text-red-400'"
                    >
                      {{ !employee.progress.exam_taken ? 'Not attempted yet' : 
                          employee.progress.exam_passed ? 'Successfully passed' : 'Did not pass' }}
                    </div>
                  </div>
                </div>

                <!-- Latest Score -->
                <div v-if="employee.progress.latest_score" class="flex items-center space-x-3">
                  <div 
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    :class="employee.progress.latest_score.passed ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'"
                  >
                    <Target class="w-5 h-5" :class="employee.progress.latest_score.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'" />
                  </div>
                  <div>
                    <div class="text-lg font-bold" :class="employee.progress.latest_score.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                      {{ employee.progress.latest_score.percentage }}%
                    </div>
                    <div class="text-xs text-secondary-text dark:text-secondary-dark-text">
                      {{ formatScore(employee.progress.latest_score.score) }}/{{ formatScore(employee.progress.latest_score.total) }} Score
                    </div>
                  </div>
                </div>
                <div v-else class="flex items-center space-x-3 opacity-50">
                  <div class="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <Target class="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">No Score</div>
                    <div class="text-xs text-gray-400">Not attempted</div>
                  </div>
                </div>

                <!-- Attempts -->
                <div class="flex items-center space-x-3">
                  <div 
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    :class="employee.progress.attempts > 0 ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-gray-800'"
                  >
                    <TrendingUp class="w-5 h-5" :class="employee.progress.attempts > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'" />
                  </div>
                  <div>
                    <div class="text-lg font-bold text-primary-text dark:text-primary-dark-text">
                      {{ employeeProgress?.module?.exam?.total_attempts 
                        ? `${employee.progress.attempts}/${employeeProgress.module.exam.total_attempts}` 
                        : employee.progress.attempts }}
                    </div>
                    <div class="text-xs text-secondary-text dark:text-secondary-dark-text">
                      {{ employee.progress.attempts === 1 ? 'Attempt' : 'Attempts' }}
                    </div>
                  </div>
                </div>

                <!-- Achievement Badge -->
                <div class="flex items-center space-x-3">
                  <div 
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    :class="employee.progress.exam_passed ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-800'"
                  >
                    <Award class="w-5 h-5" :class="employee.progress.exam_passed ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'" />
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center space-x-2">
                      <div class="text-sm font-medium" :class="employee.progress.exam_passed ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'">
                        {{ employee.progress.exam_passed ? 'Certified' : 'Not Certified' }}
                      </div>
                      <BaseTooltip
                        :text="getCertificationTooltip(employee)"
                        position="left"
                        trigger-class="text-sky-500 dark:text-sky-400"
                      />
                    </div>
                    <div class="text-xs text-secondary-text dark:text-secondary-dark-text">
                      {{ employee.progress.exam_passed ? 'Requirements met' : employee.progress.exam_taken ? 'Failed exam' : 'Pending completion' }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- No Exam Available State -->
              <div v-else class="bg-gray-50 dark:bg-dark-primary/30 rounded-lg p-4">
                <div class="flex items-center justify-center space-x-3 text-gray-500 dark:text-gray-400">
                  <AlertCircle class="w-5 h-5" />
                  <span class="text-sm">No exam available for this module</span>
                </div>
              </div>
            </div>
          </div>

          <!-- No Employees Found -->
          <div v-else-if="employeeProgress && employeeProgress.employee_progress.length === 0" class="text-center py-8">
            <div class="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
              <Users class="w-16 h-16 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
              <h5 class="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                No Employees Assigned
              </h5>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                No employees are currently assigned to this module for {{ monthData?.name }} {{ year }}
              </p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="loadingProgress" class="text-center py-8">
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <h5 class="text-lg font-medium text-blue-700 dark:text-blue-300 mb-2">
                Loading Employee Progress...
              </h5>
              <p class="text-sm text-blue-600 dark:text-blue-400">
                Fetching employee assignments and exam results
              </p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="progressError" class="text-center py-8">
            <div class="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8">
              <AlertCircle class="w-16 h-16 text-red-400 dark:text-red-500 mx-auto mb-4" />
              <h5 class="text-lg font-medium text-red-700 dark:text-red-300 mb-2">
                Failed to Load Progress
              </h5>
              <p class="text-sm text-red-600 dark:text-red-400 mb-4">
                {{ progressError }}
              </p>
              <button
                @click="refreshEmployeeProgress"
                class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
  </BaseModal>
</template>

<script setup lang="ts">
import type { ModuleRotation } from '@/services/modules'
import type { ModuleProgressData } from '@/services/employee-assignment'
import { EmployeeAssignmentService } from '@/services/employee-assignment'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseTooltip from '@/components/common/BaseTooltip.vue'
import { 
  Calendar,
  Clock,
  Info,
  Users,
  User,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock3,
  UserCheck,
  UserX,
  Award,
  Target,
  TrendingUp,
  Mail
} from 'lucide-vue-next'
import { ref, watch } from 'vue'

// Define month interface
interface MonthData {
  number: number
  name: string
  module: ModuleRotation | null
  isCurrentMonth: boolean
}

// Props
interface Props {
  show: boolean
  monthData: MonthData | null
  year: number
}

const props = defineProps<Props>()

// Emits
defineEmits<{
  close: []
}>()

// Reactive data for employee progress
const employeeProgress = ref<ModuleProgressData | null>(null)
const loadingProgress = ref(false)
const progressError = ref<string | null>(null)

// Load employee progress when modal is shown and has module data
watch(
  [() => props.show, () => props.monthData?.module?.module_id, () => props.year],
  ([show, moduleId, year]) => {
    if (show && moduleId) {
      loadEmployeeProgress(moduleId, year)
    } else {
      employeeProgress.value = null
      progressError.value = null
    }
  },
  { immediate: true }
)

// Load employee progress
const loadEmployeeProgress = async (moduleId: number, year: number) => {
  if (loadingProgress.value) return
  
  try {
    loadingProgress.value = true
    progressError.value = null
    
    const response = await EmployeeAssignmentService.getModuleEmployeeProgress(
      moduleId, 
      year, 
      props.monthData?.number
    )
    
    employeeProgress.value = response.data
  } catch (error: any) {
    console.error('Failed to load employee progress:', error)
    progressError.value = error.message || 'Failed to load employee progress data'
    employeeProgress.value = null
  } finally {
    loadingProgress.value = false
  }
}

// Refresh employee progress
const refreshEmployeeProgress = async () => {
  if (props.monthData?.module?.module_id) {
    await loadEmployeeProgress(props.monthData.module.module_id, props.year)
  }
}

// Helper function to get month names
const getMonthNames = (months: number[]): string[] => {
  const monthNames = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return months.map(month => monthNames[month] || 'Unknown')
}

// Utility methods
const getModuleStatusColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg shadow-green-200 dark:shadow-green-900/50'
  if (module.is_future) return 'bg-gradient-to-r from-blue-400 to-indigo-500 shadow-lg shadow-blue-200 dark:shadow-blue-900/50'
  return 'bg-gradient-to-r from-gray-400 to-slate-500 shadow-lg shadow-gray-200 dark:shadow-gray-900/50'
}

const getModuleStatusTextColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'text-green-700 dark:text-green-300'
  if (module.is_future) return 'text-blue-700 dark:text-blue-300'
  return 'text-gray-600 dark:text-gray-400'
}

const getModuleStatusText = (module: ModuleRotation): string => {
  if (module.is_current) return 'Current Module'
  if (module.is_future) return 'Upcoming Module'
  return 'Past Module'
}

const getProgressBarColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-gradient-to-r from-green-500 to-emerald-500'
  if (module.is_future) return 'bg-gradient-to-r from-blue-500 to-indigo-500'
  return 'bg-gradient-to-r from-gray-400 to-slate-400'
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

const getDuration = (startDate: string, endDate: string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return `${diffDays} days`
}

const getMonthProgress = (module: ModuleRotation): number => {
  if (module.is_past) return 100
  if (module.is_future) return 0
  
  // For current module, calculate actual progress
  const start = new Date(module.start_date)
  const end = new Date(module.end_date)
  const now = new Date()
  
  const total = end.getTime() - start.getTime()
  const elapsed = now.getTime() - start.getTime()
  
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
}

const getProgressDescription = (module: ModuleRotation): string => {
  if (module.is_past) return 'Module duration completed'
  if (module.is_future) return 'Module duration not started'
  
  const progress = getMonthProgress(module)
  if (progress < 25) return 'Early in the month'
  if (progress < 50) return 'Quarter way through month'
  if (progress < 75) return 'Halfway through month'
  return 'Nearing end of month'
}

// Employee status helper functions
const getEmployeeStatusClass = (employee: any): string => {
  if (!employee.has_access) {
    return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
  }
  
  if (!employee.progress.exam_available) {
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600'
  }
  
  if (!employee.progress.exam_taken) {
    return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600'
  }
  
  if (employee.progress.exam_passed) {
    return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600'
  }
  
  return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600'
}

const getEmployeeStatusIcon = (employee: any) => {
  if (!employee.has_access) return UserX
  if (!employee.progress.exam_available) return User
  if (!employee.progress.exam_taken) return Clock3
  if (employee.progress.exam_passed) return CheckCircle2
  return XCircle
}

const getEmployeeStatusText = (employee: any): string => {
  if (!employee.has_access) return 'No Access'
  if (!employee.progress.exam_available) return 'Assigned'
  if (!employee.progress.exam_taken) return 'Pending'
  if (employee.progress.exam_passed) return 'Completed'
  return 'Failed'
}

// Get certification tooltip text based on employee progress
const getCertificationTooltip = (employee: any): string => {
  if (!employee.progress.exam_available) {
    return 'No exam is available for this module'
  }
  
  if (!employee.progress.exam_taken) {
    return 'Employee has not yet attempted the exam'
  }
  
  if (employee.progress.exam_passed) {
    return 'This employee has completed the module and passed the exam'
  }
  
  return 'Employee attempted the exam but did not pass'
}

// Handle profile image loading errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  // Hide the broken image by setting display to none
  // The fallback initials will be shown instead
  img.style.display = 'none'
}

// Ensure scores are displayed as whole numbers and calculate safe percentages
const formatScore = (score?: number | null) => {
  const n = Number(score ?? 0)
  if (Number.isNaN(n)) return 0
  return Math.round(n)
}
</script>

<style scoped>
/* Responsive adjustments for screens <= 510px */
@media (max-width: 510px) {
  /* Stack employee header and badge vertically */
  .employee-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .employee-info {
    margin-bottom: 0.5rem;
  }

  .employee-badge {
    display: flex;
    justify-content: flex-start;
    width: 100%;
  }

  .employee-badge .flex {
    margin-left: 0; /* ensure badge aligns with left edge */
  }

  /* Enlarge icon for completed/failed badges for better visibility on small screens */
  .status-completed .status-icon,
  .status-failed .status-icon {
    width: 1.2rem !important; /* 19.2px */
    height: 1.2rem !important;
    margin-right: 0.5rem !important;
  }

  /* Slightly increase card padding to accommodate icon size */
  .rounded-xl.p-5 {
    padding: 1.25rem !important;
  }
}
</style>
